import * as GAME from 'vibegame';
import { Transform, Parent } from 'vibegame/transforms';
import { Renderer } from 'vibegame/rendering';

export const TextInputParticle = GAME.defineComponent({
  index: GAME.Types.ui8,
  targetY: GAME.Types.f32,
});

export const TextInputManager = GAME.defineComponent({
  initialized: GAME.Types.ui8,
  t: GAME.Types.f32,
  startX: GAME.Types.f32,
  endX: GAME.Types.f32,
});

const PARTICLE_COUNT = 6;
const TARGET_Y_POSITIONS = [-3, 0, 3];

const managerQuery = GAME.defineQuery([TextInputManager, Transform]);
const particleQuery = GAME.defineQuery([TextInputParticle, Transform, Renderer]);

const TextInputSetupSystem: GAME.System = {
  group: 'setup',
  update(state) {
    for (const eid of managerQuery(state.world)) {
      if (TextInputManager.initialized[eid]) continue;
      TextInputManager.initialized[eid] = 1;

      const startX = TextInputManager.startX[eid] || -20;

      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const particle = state.createEntity();
        const targetY = TARGET_Y_POSITIONS[i % TARGET_Y_POSITIONS.length];

        state.addComponent(particle, Transform, {
          posX: startX - i * 1.5,
          posY: targetY + (Math.random() - 0.5) * 2,
          posZ: 0,
          scaleX: 0,
          scaleY: 0,
          scaleZ: 0,
        });

        state.addComponent(particle, Renderer, {
          shape: 1,
          sizeX: 0.5,
          sizeY: 0.5,
          sizeZ: 0.5,
          color: 0x44aa88,
          visible: 1,
        });

        state.addComponent(particle, TextInputParticle, {
          index: i,
          targetY,
        });

        state.addComponent(particle, Parent, { entity: eid });
      }
    }
  },
};

const TextInputAnimationSystem: GAME.System = {
  group: 'simulation',
  update(state) {
    for (const managerEid of managerQuery(state.world)) {
      const t = TextInputManager.t[managerEid];
      const startX = TextInputManager.startX[managerEid] || -20;
      const endX = TextInputManager.endX[managerEid] || -9;

      for (const eid of particleQuery(state.world)) {
        const index = TextInputParticle.index[eid];
        const targetY = TextInputParticle.targetY[eid];

        const appearDelay = index * 0.1;
        const particleT = Math.max(0, Math.min(1, (t - appearDelay) / 0.5));

        const eased = particleT * particleT * (3 - 2 * particleT);

        const scale = Math.min(1, particleT * 3);
        Transform.scaleX[eid] = scale;
        Transform.scaleY[eid] = scale;
        Transform.scaleZ[eid] = scale;

        const particleStartX = startX - index * 1.5;
        Transform.posX[eid] = particleStartX + (endX - particleStartX) * eased;

        const startY = targetY + (index % 2 === 0 ? 2 : -2);
        Transform.posY[eid] = startY + (targetY - startY) * eased;
      }
    }
  },
};

export const TextInputPlugin: GAME.Plugin = {
  components: { TextInputParticle, TextInputManager },
  systems: [TextInputSetupSystem, TextInputAnimationSystem],
  config: {
    defaults: {
      'text-input-manager': {
        initialized: 0,
        t: 0,
        startX: -20,
        endX: -9,
      },
    },
  },
};
